import { useState } from "react";
import { Button, ErrorText, Field, Panel, SelectField, Spinner } from "../../components/ui";
import { fmtAmount, fmtDate, fmtRate } from "../../lib/format";
import { useCreateSecurity, useSecurities, useUpdateSecurity } from "./hooks";
import type { CreateSecurityInput } from "./api";

const TYPES = ["action", "obligation", "fcp", "sicav", "droit"];
const STATUSES = ["listed", "suspended", "delisted"] as const;

/** "6,5" → "6.5" — the API takes dot decimals. */
const decimal = (v: string | null | undefined) => (v ? v.trim().replace(",", ".") : null);

const EMPTY: CreateSecurityInput = { ticker: "", name: "", type: "action", sector: "" };

export function SecuritiesPage() {
  const { data, isLoading, error } = useSecurities();
  const create = useCreateSecurity();
  const update = useUpdateSecurity();
  const [form, setForm] = useState<CreateSecurityInput | null>(null);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    const isBond = form.type === "obligation";
    create.mutate(
      {
        ...form,
        ticker: form.ticker.trim().toUpperCase(),
        sector: form.sector || null,
        faceValue: isBond ? decimal(form.faceValue) : null,
        nominalRate: isBond ? decimal(form.nominalRate) : null,
        durationMonths: isBond ? form.durationMonths : null,
      },
      { onSuccess: () => setForm(null) },
    );
  }

  return (
    <Panel
      title="Titres"
      actions={
        <Button onClick={() => setForm(form ? null : { ...EMPTY })}>
          {form ? "Annuler" : "Nouveau titre"}
        </Button>
      }
    >
      {form && (
        <form className="inline-form" onSubmit={submit}>
          <Field
            label="Ticker"
            value={form.ticker}
            onChange={(e) => setForm({ ...form, ticker: e.target.value })}
            required
          />
          <Field
            label="Nom"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <SelectField
            label="Type"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          >
            {TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </SelectField>
          {form.type === "obligation" && (
            <>
              <Field
                label="Prix unitaire d'acquisition (FCFA)"
                inputMode="decimal"
                value={form.faceValue ?? ""}
                onChange={(e) => setForm({ ...form, faceValue: e.target.value })}
                required
              />
              <Field
                label="Taux nominal (%/an)"
                inputMode="decimal"
                placeholder="ex. 6,5"
                value={form.nominalRate ?? ""}
                onChange={(e) => setForm({ ...form, nominalRate: e.target.value })}
                required
              />
              <Field
                label="Durée (mois)"
                type="number"
                min={1}
                step={1}
                value={form.durationMonths ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    durationMonths: e.target.value ? Number(e.target.value) : null,
                  })
                }
                required
              />
            </>
          )}
          <Field
            label="Secteur"
            value={form.sector ?? ""}
            onChange={(e) => setForm({ ...form, sector: e.target.value })}
          />
          <Field
            label="ISIN"
            value={form.isin ?? ""}
            onChange={(e) => setForm({ ...form, isin: e.target.value || null })}
          />
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "…" : "Créer"}
          </Button>
          <ErrorText error={create.error} />
        </form>
      )}

      <ErrorText error={error} />
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Nom</th>
                <th>Type</th>
                <th>Secteur</th>
                <th>Obligation</th>
                <th>Statut</th>
                <th>Modifié</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((s) => (
                <tr key={s.id}>
                  <td>
                    <strong>{s.ticker}</strong>
                  </td>
                  <td>{s.name}</td>
                  <td>{s.type}</td>
                  <td>{s.sector ?? "—"}</td>
                  <td>
                    {s.type === "obligation" && s.nominalRate
                      ? `${fmtAmount(s.faceValue)} FCFA · ${fmtRate(s.nominalRate)} · ${s.durationMonths} mois`
                      : "—"}
                  </td>
                  <td>
                    <select
                      value={s.status}
                      disabled={update.isPending}
                      onChange={(e) =>
                        update.mutate({ id: s.id, patch: { status: e.target.value } })
                      }
                    >
                      {STATUSES.map((st) => (
                        <option key={st}>{st}</option>
                      ))}
                    </select>
                  </td>
                  <td>{fmtDate(s.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
